import { Autocomplete, TextField as MuiTextField, Chip, Link } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useState, useCallback, useEffect, useRef } from 'react'
import { apiGet } from '../../api/client'
import type { FieldMeta, ApiResponse } from '@crm/shared'

interface LookupFieldProps {
  field: FieldMeta
  value: unknown
  mode: 'view' | 'edit'
  onChange?: (value: unknown) => void
  error?: string
  disabled?: boolean
  hideLabel?: boolean
}

interface LookupOption {
  id: string
  label: string
}

function getConfig(field: FieldMeta): { target_object_id: string; display_field: string } | null {
  if (!field.config) return null
  try {
    return JSON.parse(field.config as unknown as string)
  } catch {
    return null
  }
}

export function LookupField({ field, value, mode, onChange, error, disabled, hideLabel }: LookupFieldProps) {
  const navigate = useNavigate()
  const config = getConfig(field)
  const [options, setOptions] = useState<LookupOption[]>([])
  const [inputValue, setInputValue] = useState('')
  const [displayValue, setDisplayValue] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const searchRecords = useCallback(async (keyword?: string) => {
    if (!config?.target_object_id) return
    try {
      const params = new URLSearchParams({ limit: '20' })
      if (keyword?.trim()) params.set('keyword', keyword.trim())
      const result = await apiGet<{ data: Array<{ id: string; data: Record<string, unknown> }> }>(
        `/objects/${config.target_object_id}/records?${params.toString()}`,
      )
      const displayField = config.display_field || 'name'
      const items = (result.data ?? []).map((r) => {
        const label = String(r.data[displayField] ?? r.id)
        return { id: r.id, label }
      })
      setOptions(items)
    } catch { /* ignore */ }
  }, [config])

  const debouncedSearch = useCallback((keyword: string) => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => searchRecords(keyword), 300)
  }, [searchRecords])

  useEffect(() => {
    return () => clearTimeout(debounceRef.current)
  }, [])

  const loadDisplayValue = useCallback(async () => {
    if (!config?.target_object_id || !value) return
    try {
      const result = await apiGet<ApiResponse<{ id: string; data: Record<string, unknown> }>>(
        `/objects/${config.target_object_id}/records/${value}`,
      )
      const displayField = config.display_field || 'name'
      setDisplayValue(String(result.data.data[displayField] ?? value))
    } catch {
      setDisplayValue(String(value))
    }
  }, [config, value])

  useEffect(() => {
    if (mode === 'view' && value && config?.target_object_id) {
      loadDisplayValue()
    }
  }, [mode, value, config, loadDisplayValue])

  if (mode === 'view') {
    if (value == null) return <span />
    if (config?.target_object_id) {
      return (
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate(`/o/${config.target_object_id}/${value}`)}
          sx={{ cursor: 'pointer' }}
        >
          {displayValue || String(value)}
        </Link>
      )
    }
    return <span>{String(value)}</span>
  }

  return (
    <Autocomplete
      size="small"
      disabled={disabled}
      options={options}
      getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.label}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      value={value ? { id: String(value), label: displayValue || String(value) } : null}
      inputValue={inputValue}
      onInputChange={(_, newInput) => {
        setInputValue(newInput)
        debouncedSearch(newInput)
      }}
      onChange={(_, newVal) => {
        onChange?.(newVal ? newVal.id : undefined)
        if (newVal) setDisplayValue(newVal.label)
        else setDisplayValue('')
      }}
      onFocus={() => {
        if (value && !displayValue) loadDisplayValue()
        if (options.length === 0) searchRecords()
      }}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          label={hideLabel ? undefined : field.label}
          error={!!error}
          helperText={error}
          required={!!field.is_required}
        />
      )}
    />
  )
}
